'use client'

import React, { useState, useEffect, useCallback } from 'react'
import { useGetFlightOffersQuery } from '@/src/api/rtkQuery/offersApi'
import HomepageCarouselComponent from '@/src/components/common/home/homepageCarousel/homepageCarousel'
import { getCurrentLocale } from '@/src/utils/rtkQuery/getCurrentLocale'

interface HomepageCarouselProps {
    title: string
}

export const HomepageCarousel = ({ title }: HomepageCarouselProps) => {
    const locale = getCurrentLocale()
    const { data: offersData, error } = useGetFlightOffersQuery({
        domain: 'FSA',
        homepageBanner: true
    })

    const [currentIndex, setCurrentIndex] = useState(0)
    const [isAutoPlaying, setIsAutoPlaying] = useState(true)

    // Auto-scroll every 3 seconds
    useEffect(() => {
        if (!offersData?.offers?.length || !isAutoPlaying) return
        
        const timer = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % offersData.offers.length)
        }, 3000)

        return () => clearInterval(timer)
    }, [offersData?.offers?.length, isAutoPlaying])

    // Manual navigation
    const nextSlide = useCallback(() => {
        if (!offersData?.offers?.length) return
        setCurrentIndex((prev) => (prev + 1) % offersData.offers.length)
    }, [offersData?.offers?.length])

    const prevSlide = useCallback(() => {
        if (!offersData?.offers?.length) return
        setCurrentIndex((prev) => (prev - 1 + offersData.offers.length) % offersData.offers.length)
    }, [offersData?.offers?.length])

    // Pause auto-play on hover
    const handleMouseEnter = useCallback(() => {
        setIsAutoPlaying(false)
    }, [])

    const handleMouseLeave = useCallback(() => {
        setIsAutoPlaying(true)
    }, [])

    const handleOfferClick = useCallback((slug: string | undefined) => {
        if (slug) {
            window.open(`/${locale}/offers/${slug}`, '_blank')
        }
    }, [locale])

    if (error || !offersData?.offers?.length) {
        return null
    }

    return (
        <HomepageCarouselComponent
            title={title}
            offers={offersData.offers}
            currentIndex={currentIndex}
            onPrev={prevSlide}
            onNext={nextSlide}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onOfferClick={handleOfferClick}
        />
    )
}

export default HomepageCarousel
